import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import ECCWorkPermitPDFViewer from './ECCWorkPermitPDFViewer';
import ECCNominationApprovalModal from './ECCNominationApprovalModal';
import ModalAddScope from '../turbinmanagment/ModalAddScope';
import { UserContext } from '../../contexts/UserContext';
import {
  ArrowLeft, ChevronRight, FileText, MapPin, Calendar,
  ChevronUp, ChevronDown, Clock, Briefcase, Users, AlertCircle
} from 'lucide-react';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const WorkPermitApprovalModal = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [workpermit, setWorkpermit] = useState(null);
  const [nominations, setNominations] = useState([]);
  const [workscopes, setWorkscopes] = useState([]);
  const [scopesOfWork, setScopesOfWork] = useState([]);
  const [relevantNominations, setRelevantNominations] = useState([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [status, setStatus] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [updatedWorkPermit, setUpdatedWorkPermit] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [instApproved, setInstApproved] = useState(false);
  const [instructions, setInstructions] = useState([]);
  const [isEditingScope, setIsEditingScope] = useState(false);
  const [selectedScopeId, setSelectedScopeId] = useState('');
  const [userExpanded, setUserExpanded] = useState(null);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [isCommentExpanded, setIsCommentExpanded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [isNominationModalOpen, setIsNominationModalOpen] = useState(false);
  const [selectedNomination, setSelectedNomination] = useState(null);
  const [teamMemberQualifications, setTeamMemberQualifications] = useState({});
  const [selectedInstructionId, setSelectedInstructionId] = useState(null);
  const [showIsolationFlowModal, setShowIsolationFlowModal] = useState(false);
  const [isolationFlowData, setIsolationFlowData] = useState(null);
  const [loadingIsolationFlow, setLoadingIsolationFlow] = useState(false);
  const [showRevertModal, setShowRevertModal] = useState(false);

  // Turbine editing states
  const [turbines, setTurbines] = useState([]);
  const [isEditingTurbine, setIsEditingTurbine] = useState(false);
  const [selectedTurbineId, setSelectedTurbineId] = useState(null);
  const [savingTurbine, setSavingTurbine] = useState(false);

  const refreshNominations = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API_BASE_URL}/nomination/?user_id=${selectedUser?.id}&status=approved`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Zaktualizuj relevantNominations lub wywołaj filterNominations
      const filtered = filterNominations(response.data || [], workpermit);
      setRelevantNominations(filtered);
    } catch (error) {
      console.error('Błąd podczas odświeżania nominacji:', error);
    }
  };

  // Fetch available turbines for editing
  const fetchTurbines = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/turbines/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTurbines(response.data || []);
    } catch (error) {
      console.error('Error fetching turbines:', error);
    }
  };

  // Save turbine selection
  const handleSaveTurbine = async () => {
    if (!selectedTurbineId || !workpermit) return;

    setSavingTurbine(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.patch(
        `${API_BASE_URL}/workpermit/${workpermit.id}/`,
        { turbine: selectedTurbineId },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      // Update local workpermit state with new data
      setWorkpermit(response.data);
      setIsEditingTurbine(false);
      setSelectedTurbineId(null);
    } catch (error) {
      console.error('Error saving turbine:', error);
      alert('Failed to save turbine. Please try again.');
    } finally {
      setSavingTurbine(false);
    }
  };

  const checkTeamMemberQualifications = async () => {
    if (!workpermit || !workpermit.team_data?.users || !workscopes.length) {
      return;
    }

    const qualificationStatus = {};
    const token = localStorage.getItem('access_token');

    for (const user of workpermit.team_data.users) {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/nomination/?user_id=${user.id}&status=approved`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const userNominations = response.data || [];
        const workPermitWorkScopeId = workpermit.work_scope;
        const userRole = getUserRole(user.id);

        let roleCondition;
        switch (userRole) {
          case 'Supervisor':
            roleCondition = (ws) => ws.is_kierujacy === true;
            break;
          case 'Preparer':
            roleCondition = (ws) => ws.is_dopuszczajacy === true;
            break;
          case 'Team Member':
            roleCondition = () => true;
            break;
          default:
            roleCondition = () => true;
        }

        const filteredNominations = filterNominations(
          userNominations, 
          workscopes, 
          workPermitWorkScopeId, 
          workpermit, 
          roleCondition
        );

        qualificationStatus[user.id] = {
          hasValidNomination: filteredNominations.length > 0,
          nominations: filteredNominations
        };
      } catch (error) {
        console.error(`Błąd podczas sprawdzania kwalifikacji dla użytkownika ${user.id}:`, error);
        qualificationStatus[user.id] = {
          hasValidNomination: false,
          nominations: []
        };
      }
    }

    setTeamMemberQualifications(qualificationStatus);
  };

  // Funkcja zamykania modala nominacji
  const handleCloseNominationModal = () => {
    setIsNominationModalOpen(false);
    setSelectedNomination(null);
  };

  const handleNominationClick = (nomination, matchingWorkScopes, event) => {
    event.stopPropagation();
    console.log('Kliknięto nominację:', {
      id: nomination.id,
      nomination_number: nomination.nomination_number,
      matchingWorkScopes: matchingWorkScopes.map((ws) => ({
        id: ws.id,
        name: ws.name || 'N/A',
        categories: (ws.categories || []).map((cat) => ({
          id: cat.id,
          name: cat.name || 'N/A',
        })),
      })),
    });
    // Otwórz modal z wybraną nominacją
    setSelectedNomination(nomination);
    setIsNominationModalOpen(true);
  };

  const fetchInstructions = async (turbineId, scopeId) => {
    if (!turbineId || !scopeId) {
      console.log('Brak ID turbiny lub zakresu prac');
      return [];
    }

    try {
      console.log('Pobieranie instrukcji dla turbiny i zakresu:', turbineId);
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`${API_BASE_URL}/instructions-latest/`, {
        params: { turbine_id: turbineId, scope: scopeId },
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("Pobrane dane instrukcji:", response.data);
      return response.data;
    } catch (error) {
      console.error("Błąd podczas pobierania instrukcji:", error);
      return [];
    }
  };

  useEffect(() => {
    const workpermitId = searchParams.get('workpermit_id');
    if (!workpermitId) {
      console.error('Brak workpermit_id w URL');
      return;
    }

    const fetchWorkpermitAndInstructions = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          console.error('Brak tokenu autoryzacyjnego');
          return;
        }
        const workpermitResponse = await axios.get(`${API_BASE_URL}/workpermit/${workpermitId}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log('Pobrane dane workpermit:', workpermitResponse.data);
        setWorkpermit(workpermitResponse.data);
        setStatus(workpermitResponse.data.status);
        setUpdatedWorkPermit(workpermitResponse.data);

        const instructionsData = await fetchInstructions(
          workpermitResponse.data.turbine,
          workpermitResponse.data.work_scope
        );
        setInstructions(instructionsData);
      } catch (error) {
        console.error('Błąd podczas pobierania workpermit lub instrukcji:', error);
        if (error.response) {
          console.error('Szczegóły błędu:', error.response.data);
        }
      }
    };

    fetchWorkpermitAndInstructions();
  }, [searchParams]);

  // Auto-open Isolation Flow modal if tab=isolation in URL (only if has submissions)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'isolation' && workpermit?.isolation_flow_status?.has_submission) {
      setShowIsolationFlowModal(true);
    }
  }, [searchParams, workpermit]);

  // Fetch turbines for editing (only for pending status)
  useEffect(() => {
    if (workpermit?.status === 'signed') {
      fetchTurbines();
    }
  }, [workpermit?.status]);

  useEffect(() => {
    const fetchNominations = async () => {
      if (!selectedUser) {
        setNominations([]);
        return;
      }

      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(
          `${API_BASE_URL}/nomination/?user_id=${selectedUser.id}&status=approved`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setNominations(response.data);

      } catch (error) {
        console.error("Błąd podczas pobierania nominacji:", error);
        setNominations([]);
      }
    };

    fetchNominations();
  }, [selectedUser]);

  useEffect(() => {
    const fetchWorkscopes = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${API_BASE_URL}/workscopes/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setWorkscopes(response.data);
        console.log("workscopes", response.data);
      } catch (error) {
        console.error("Błąd podczas pobierania workscopes:", error);
        setWorkscopes([]);
      }
    };

    fetchWorkscopes();
  }, []);

  const fetchScopesOfWork = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${API_BASE_URL}/scopeofworks/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setScopesOfWork(response.data);
        console.log("scopesofwork", response.data);
      } catch (error) {
        console.error("Błąd podczas pobierania scopeofwork:", error);
        setScopesOfWork([]);
      }
    };

  useEffect(() => {
    fetchScopesOfWork();
  }, []);

  useEffect(() => {
    if (workpermit && workscopes.length > 0) {
      checkTeamMemberQualifications();
    }
  }, [workpermit, workscopes]);

  const filterNominations = (nominations, workscopes, workPermitWorkScopeId, workpermit, roleCondition) => {
    console.log('filterNominations - Dane wejściowe:', {
      nominationsCount: nominations.length,
      workscopesCount: workscopes.length,
      workPermitWorkScopeId,
      workpermit: {
        id: workpermit.id,
        work_permit_number: workpermit.work_permit_number,
        turbine_model: workpermit.turbine_data?.model,
      },
      roleCondition: roleCondition.toString(),
    });

    // Krok 1: Filtrowanie workscopes
    const matchingWorkscopes = workscopes.filter((ws) => {
      const scopes = ws.scopes || [];
      const hasWorkScopeId = scopes.includes(workPermitWorkScopeId);
      const roleConditionResult = roleCondition(ws);
      console.log('Filtr workscope:', {
        workscopeId: ws.id,
        scopes,
        hasWorkScopeId,
        roleConditionResult,
        is_kierujacy: ws.is_kierujacy,
        is_dopuszczajacy: ws.is_dopuszczajacy,
      });
      return hasWorkScopeId && roleConditionResult;
    });
    const matchingWorkscopeIds = matchingWorkscopes.map((ws) => ws.id);
    console.log('matchingWorkscopes:', matchingWorkscopes);
    console.log('matchingWorkscopeIds:', matchingWorkscopeIds);

    // Krok 2: Filtrowanie nominacji
    const filteredNominations = nominations
      .map((nomination) => {
        const { id, work_scopes = [], work_scope_data = [], categories = [] } = nomination;
        console.log(`Sprawdzanie nominacji ID: ${id}`, {
          nominationNumber: nomination.nomination_number,
          work_scopes,
          work_scope_data: work_scope_data.map((ws) => ({ id: ws.id, name: ws.name })),
          categories,
        });

        // Sprawdzenie, czy nominacja ma pasujący workscope
        const hasMatchingWorkscope = work_scopes.some((wsId) => matchingWorkscopeIds.includes(wsId));
        console.log(`Nominacja ID: ${id} - hasMatchingWorkscope:`, hasMatchingWorkscope);
        if (!hasMatchingWorkscope) {
          console.log(`Nominacja ID: ${id} odrzucona - brak zgodnego workscope`);
          return null;
        }

        // Pobieranie modelu turbiny
        const turbineModelId = workpermit.turbine_data?.model;

        // Filtrowanie pasujących workscopes z work_scope_data
        const matchingWorkScopeData = work_scope_data
          .filter((ws) => matchingWorkscopeIds.includes(ws.id))
          .map((ws) => {
            // Filtrowanie kategorii: wspólne z nomination.categories i zgodne z modelem turbiny
            const wsCategories = (ws.categories || []).filter((cat) =>
              categories.includes(cat.id) && // Zgodność z nomination.categories
              (cat.models || []).includes(turbineModelId) // Zgodność z modelem turbiny
            );
            return {
              ...ws,
              categories: wsCategories, // Nadpisywanie kategorii tylko pasującymi
            };
          });
        console.log(`Nominacja ID: ${id} - matchingWorkScopeData:`, matchingWorkScopeData);
        if (!matchingWorkScopeData.length) {
          console.log(`Nominacja ID: ${id} odrzucona - brak danych workscope`);
          return null;
        }

        // Sprawdzenie kategorii
        const workScopeCategories = matchingWorkScopeData
          .flatMap((ws) => ws.categories || [])
          .filter((cat, index, self) => self.findIndex((c) => c.id === cat.id) === index);
        console.log(`Nominacja ID: ${id} - workScopeCategories:`, workScopeCategories);
        if (workScopeCategories.length === 0 || categories.length === 0) {
          console.log(`Nominacja ID: ${id} zaakceptowana - brak kategorii lub puste workScopeCategories`);
          return {
            nomination,
            matchingWorkScopes: matchingWorkScopeData,
          };
        }

        const hasMatchingCategory = categories.some((catId) =>
          workScopeCategories.some((wsCat) => wsCat.id === catId)
        );
        console.log(`Nominacja ID: ${id} - hasMatchingCategory:`, hasMatchingCategory);
        if (!hasMatchingCategory) {
          console.log(`Nominacja ID: ${id} odrzucona - brak zgodnej kategorii`);
          return null;
        }

        // Sprawdzenie modelu turbiny (dodatkowe zabezpieczenie)
        const categoryModels = workScopeCategories.flatMap((cat) => cat.models || []);
        console.log(`Nominacja ID: ${id} - Sprawdzanie modelu turbiny:`, {
          turbineModelId,
          categoryModels,
        });
        const isModelIncluded = categoryModels.includes(turbineModelId);
        console.log(`Nominacja ID: ${id} - isModelIncluded:`, isModelIncluded);
        if (!isModelIncluded) {
          console.log(`Nominacja ID: ${id} odrzucona - niezgodny model turbiny`);
          return null;
        }

        return {
          nomination,
          matchingWorkScopes: matchingWorkScopeData,
        };
      })
      .filter((result) => result !== null);

    // Logowanie końcowego wyniku filtrowania
    console.log('Końcowy wynik filtrowania nominacji:', {
      filteredNominationsCount: filteredNominations.length,
      filteredNominations: filteredNominations.map(({ nomination, matchingWorkScopes }) => ({
        id: nomination.id,
        nomination_number: nomination.nomination_number,
        matchingWorkScopes: matchingWorkScopes.map((ws) => ({
          id: ws.id,
          name: ws.name || 'N/A',
          categories: (ws.categories || []).map((cat) => ({
            id: cat.id,
            name: cat.name || 'N/A',
          })),
        })),
      })),
    });

    return filteredNominations;
  };

  useEffect(() => {
    console.log('useEffect dla filtrowania nominacji - Stan początkowy:', {
      workpermitExists: !!workpermit,
      selectedUser: selectedUser ? { id: selectedUser.id, name: `${selectedUser.first_name} ${selectedUser.last_name}` } : null,
      nominationsCount: nominations.length,
      workscopesCount: workscopes.length,
    });

    if (!workpermit || !selectedUser || !nominations.length || !workscopes.length) {
      console.log('Brak wymaganych danych do filtrowania nominacji:', {
        workpermitMissing: !workpermit,
        selectedUserMissing: !selectedUser,
        noNominations: !nominations.length,
        noWorkscopes: !workscopes.length,
      });
      setRelevantNominations([]);
      return;
    }

    const workPermitWorkScopeId = workpermit.work_scope;
    const userRole = getUserRole(selectedUser.id);
    console.log('Dane do filtrowania:', {
      workPermitWorkScopeId,
      userRole,
      selectedUserId: selectedUser.id,
    });

    let roleCondition;
    switch (userRole) {
      case 'Supervisor':
        roleCondition = (ws) => ws.is_kierujacy === true;
        break;
      case 'Preparer':
        roleCondition = (ws) => ws.is_dopuszczajacy === true;
        break;
      case 'Team Member':
        roleCondition = () => true;
        break;
      default:
        console.log('Nieznana rola użytkownika:', userRole);
        setRelevantNominations([]);
        return;
    }
    console.log('roleCondition wybrana dla roli:', userRole);

    const filteredNominations = filterNominations(nominations, workscopes, workPermitWorkScopeId, workpermit, roleCondition);
    console.log('Wynik filtrowania nominacji:', {
      filteredNominationsCount: filteredNominations.length,
      filteredNominations: filteredNominations.map(({ nomination }) => ({
        id: nomination.id,
        nomination_number: nomination.nomination_number,
        work_scopes: nomination.work_scopes,
      })),
    });

    setRelevantNominations(filteredNominations);
  }, [nominations, workscopes, selectedUser, workpermit]);

  const formatDateTime = (dateTime) => {
    if (!dateTime) return 'N/A';
    return new Date(dateTime).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).replace(',', '');
  };

  const getUserRole = (userId) => {
    if (!workpermit || !workpermit.team_data) return 'Team Member';
    const { team_leader, person_responsible_for_preparation_of_work_place } = workpermit.team_data;
    if (userId === team_leader) return 'Supervisor';
    if (userId === person_responsible_for_preparation_of_work_place) return 'Preparer';
    return 'Team Member';
  };

  const toggleUserExpansion = (userId) => {
    const isAlreadyExpanded = userExpanded === userId;
    setUserExpanded(isAlreadyExpanded ? null : userId);

    const user = workpermit?.team_data?.users?.find(user => user.id === userId);
    setSelectedUser(isAlreadyExpanded ? null : user || null);
  };

  const handleCheckInstruction = () => {
    const updatedPermit = {
      ...workpermit,
      approve_date: new Date().toISOString(),
      approver_name: `${user.first_name} ${user.last_name}`
    };
    setUpdatedWorkPermit(updatedPermit);
    setActiveTab('instruction');
  };

  const handleWriteInstruction = () => {
    const turbineId = workpermit?.turbine;
    const workScopeId = workpermit?.work_scope;
    const workpermitId = workpermit?.id;
    navigate(`/ecc/turbins?workpermitId=${workpermitId}&turbineId=${turbineId}&workScopeId=${workScopeId}`);
  };

  const handleConfirmInstruction = () => {
    setInstApproved(true);
  };

  const handleApprove = async () => {
    if (!workpermit || !pdfBlob) {
      console.error("Brak workpermit lub PDF do zatwierdzenia.");
      return;
    }

    try {
      if (!user || !user.id) {
        console.error("Brak danych zalogowanego użytkownika z kontekstu.");
        return;
      }

      const token = localStorage.getItem('access_token');
      if (!token) {
        console.error("Brak tokenu autoryzacyjnego.");
        return;
      }

      const formData = new FormData();
      formData.append('approve_date', updatedWorkPermit.approve_date);
      formData.append('status', 'approved');
      formData.append('approver', user.id);
      formData.append('work_permit_number', workpermit.work_permit_number);
      formData.append('attachments', pdfBlob, `workpermit_${workpermit.work_permit_number}.pdf`);
      if (selectedInstructionId) {
        formData.append('instruction', selectedInstructionId);
        // Save instruction snapshot - frozen copy of instruction data at approval time
        const selectedInstruction = instructions.find(i => i.id === selectedInstructionId);
        if (selectedInstruction) {
          formData.append('instruction_snapshot', JSON.stringify(selectedInstruction));
        }
      }

      const response = await axios.put(
        `${API_BASE_URL}/workpermit/${workpermit.id}/`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );

      console.log("Workpermit zatwierdzona z PDF:", response.data);
      handleClose();
    } catch (error) {
      console.error("Błąd podczas zatwierdzania workpermit:", error);
      if (error.response) {
        console.error("Szczegóły błędu:", error.response.data);
      }
    }
  };

  const handleReject = async () => {
    if (!workpermit) return;
    if (!rejectReason.trim()) {
      alert("Please provide a reason for rejection.");
      return;
    }
    try {
      if (!user || !user.id) {
        console.error("Brak danych zalogowanego użytkownika z kontekstu.");
        return;
      }

      const token = localStorage.getItem('access_token');
      if (!token) {
        console.error("Brak tokenu autoryzacyjnego.");
        return;
      }

      const payload = {
        rejected_date: new Date().toISOString(),
        status: "rejected",
        approver: user.id,
        comment: rejectReason,
        work_permit_number: workpermit.work_permit_number,
      };

      const response = await axios.put(
        `${API_BASE_URL}/workpermit/${workpermit.id}/`,
        payload,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      console.log("Workpermit odrzucony:", response.data);
      handleClose();
    } catch (error) {
      console.error("Błąd podczas odrzucania workpermit:", error);
      if (error.response) {
        console.error("Szczegóły błędu:", error.response.data);
      }
    }
  };

  const handleRevertToPending = async () => {
    if (!workpermit) return;

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.error("Brak tokenu autoryzacyjnego.");
        return;
      }

      const payload = {
        work_permit_number: workpermit.work_permit_number, // Required field
        status: "signed", // 'signed' is the pending status in this system
        approver: null,
        approve_date: null,
        instruction: null,
        instruction_snapshot: null,
      };

      const response = await axios.put(
        `${API_BASE_URL}/workpermit/${workpermit.id}/`,
        payload,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      console.log("Workpermit reverted to pending:", response.data);
      // Refresh workpermit data
      setWorkpermit(response.data);
      setStatus(response.data.status);
    } catch (error) {
      console.error("Błąd podczas cofania workpermit do pending:", error);
      if (error.response) {
        console.error("Szczegóły błędu:", error.response.data);
      }
    }
  };

  const handleClose = () => {
    setSelectedUser(null);
    setNominations([]);
    setRelevantNominations([]);
    setRejectReason('');
    setShowRejectModal(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl); // Zwolnienie pamięci
      setPreviewUrl(null);
    }
    setPdfBlob(null);
    setUpdatedWorkPermit(null);
    setInstApproved(false);
    setActiveTab('details');
    setInstructions([]);
    setIsEditingScope(false);
    setSelectedScopeId('');
    setPreviewExpanded(false);
    setIsCommentExpanded(false);
    setSelectedInstructionId(null);
    navigate('/ecc/workpermits/pending');
  };

  const handlePdfGenerated = (blob, instructionId) => {
    setPdfBlob(blob);
    setSelectedInstructionId(instructionId);
  };

  const handleShowAttachment = () => {
    if (workpermit.attachments) {
      window.open(workpermit.attachments, '_blank');
    }
  };

  const handleChangeScope = () => {
    setIsEditingScope(true);
  };

  const handleConfirmScope = async () => {
    if (!selectedScopeId) {
      alert("Please select a scope of work before confirming.");
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.error("Brak tokenu autoryzacyjnego.");
        return;
      }

      const response = await axios.patch(
        `${API_BASE_URL}/workpermit/${workpermit.id}/`,
        { work_scope: selectedScopeId },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      console.log("Zaktualizowano workpermit:", response.data);
      setWorkpermit(response.data);
      setUpdatedWorkPermit(response.data);

      const newInstructions = await fetchInstructions(
        response.data.turbine,
        response.data.work_scope
      );
      setInstructions(newInstructions);

      setIsEditingScope(false);
      setSelectedScopeId('');
    } catch (error) {
      console.error("Błąd podczas aktualizacji workpermit:", error);
      if (error.response) {
        console.error("Szczegóły błędu:", error.response.data);
      }
    }
  };

  const handleCancelEdit = () => {
    setIsEditingScope(false);
    setSelectedScopeId('');
  };

  const getFileNameFromUrl = (url) => {
    if (!url) return 'N/A';
    const parts = url.split('/');
    return parts[parts.length - 1] || 'N/A';
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case 'signed':
        return 'bg-brand-500 text-white';
      case 'approved':
        return 'bg-emerald-600 text-white';
      case 'rejected':
        return 'bg-red-600 text-white';
      default:
        return 'bg-graphite-600 text-white';
    }
  };

  const getStatusDisplayText = (status) => {
    switch (status) {
      case 'signed':
        return 'Pending';
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'expired':
        return 'Expired';
      case 'closed':
        return 'Closed';
      default:
        return 'Unknown';
    }
  };

  const getColumnWidths = () => {
    if (isCommentExpanded) {
      return {
        leftCol: 'w-1/2',
        middleCol: 'w-12',
        rightCol: 'w-1/2'
      };
    } else if (previewExpanded) {
      return {
        leftCol: 'w-2/6',
        middleCol: 'w-2/6',
        rightCol: 'w-4/6'
      };
    } else {
      return {
        leftCol: 'w-1/2',
        middleCol: 'w-1/2',
        rightCol: 'w-12'
      };
    }
  };

  const columnWidths = getColumnWidths();

  const handleShowInstruction = async () => {
    setPreviewExpanded(true);
    if (workpermit.attachments) {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          console.error("Brak tokenu autoryzacyjnego.");
          return;
        }

        // Pobieranie pliku jako Blob
        const response = await axios.get(workpermit.attachments, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        });

        // Tworzenie tymczasowego URL dla Blob
        const blobUrl = URL.createObjectURL(response.data);
        setPreviewUrl(blobUrl);
      } catch (error) {
        console.error("Błąd podczas pobierania załącznika:", error);
        setPreviewUrl(null);
      }
    }
  };

  // Fetch isolation flow submissions when modal opens
  const fetchIsolationFlowData = async () => {
    if (!workpermit?.id) return;

    setLoadingIsolationFlow(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API_BASE_URL}/isolation-flow/?work_permit_id=${workpermit.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('[IsolationFlow] Raw API response:', response.data);
      if (response.data?.[0]) {
        console.log('[IsolationFlow] section_signatures:', response.data[0].section_signatures);
        console.log('[IsolationFlow] section_signatures type:', typeof response.data[0].section_signatures);
        if (response.data[0].section_signatures) {
          Object.entries(response.data[0].section_signatures).forEach(([key, value]) => {
            console.log(`[IsolationFlow] Signature key="${key}", value type=${typeof value}, value preview:`,
              typeof value === 'string' ? value.substring(0, 100) + '...' : value);
          });
        }
      }
      setIsolationFlowData(response.data);
    } catch (error) {
      console.error('Error fetching isolation flow data:', error);
      setIsolationFlowData([]);
    } finally {
      setLoadingIsolationFlow(false);
    }
  };

  // Fetch isolation flow data when modal opens
  React.useEffect(() => {
    if (showIsolationFlowModal && workpermit?.id) {
      fetchIsolationFlowData();
    }
  }, [showIsolationFlowModal, workpermit?.id]);

  if (!workpermit) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-brand-500"></div>
        <span className="ml-2">Loading workpermit data...</span>
      </div>
    );
  }

  // Renderowanie komponentu dla ekranu "details"
  if (activeTab === 'details') {
    return (
      <div className="fixed inset-0 z-50 bg-steel-50 w-full h-screen flex flex-col">
        {/* Header */}
        <header className="bg-white border-b-2 border-brand-500 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={handleClose}
                className="p-2 rounded-xl text-graphite-600 hover:bg-brand-50 hover:text-brand-500 mr-3 transition-colors"
                aria-label="Go back"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-graphite-900">
                  Work Permit Approval
                </h1>
                <p className="text-sm text-graphite-500">{workpermit.work_permit_number || 'N/A'}</p>
              </div>
            </div>

            <div className="flex items-center">
              {workpermit.status === 'signed' ? (
                <div className="flex items-center bg-steel-50 rounded-full px-4 py-2">
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-brand-500 text-white flex items-center justify-center text-sm font-bold">
                      1
                    </div>
                    <span className="ml-2 text-sm font-semibold text-brand-600">Review Details</span>
                  </div>

                  <div className="w-8 h-0.5 mx-2 bg-steel-300"></div>

                  <div className="flex items-center opacity-50">
                    <div className="w-8 h-8 rounded-full bg-steel-200 text-graphite-500 flex items-center justify-center text-sm font-bold">
                      2
                    </div>
                    <span className="ml-2 text-sm font-medium text-graphite-500">Instructions</span>
                  </div>

                  <div className="w-8 h-0.5 mx-2 bg-steel-300"></div>

                  <div className="flex items-center opacity-50">
                    <div className="w-8 h-8 rounded-full bg-steel-200 text-graphite-500 flex items-center justify-center text-sm font-bold">
                      3
                    </div>
                    <span className="ml-2 text-sm font-medium text-graphite-500">Approve</span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    className="px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 font-medium"
                    onClick={handleShowInstruction}
                  >
                    Show Instruction
                  </button>
                  <button
                    className={`px-4 py-2 mr-8 rounded-full font-medium flex items-center gap-2 ${
                      workpermit?.isolation_flow_status?.has_submission
                        ? 'bg-brand-500 text-white hover:bg-brand-600'
                        : 'bg-steel-300 text-graphite-500 cursor-not-allowed'
                    }`}
                    onClick={() => setShowIsolationFlowModal(true)}
                    disabled={!workpermit?.isolation_flow_status?.has_submission}
                    title={workpermit?.isolation_flow_status?.has_submission ? 'View Isolation Flow' : 'No Isolation Flow submissions'}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                    </svg>
                    Isolation Flow
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main content */}
        <div className="flex-1 p-4 overflow-hidden">
          <div className="flex gap-4 h-full">
            {/* Work Permit Details Panel */}
            <div className={`${columnWidths.leftCol} bg-white rounded-2xl border border-steel-200 transition-all duration-300 flex flex-col shadow-sm overflow-hidden`}>
              <div className="w-full flex items-center justify-between p-4 text-left bg-gradient-to-r from-brand-50 to-white border-b border-brand-200">
                <div className="flex items-center">
                  <div className="p-2 bg-brand-500 rounded-lg mr-3">
                    <FileText className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-lg font-semibold text-graphite-900">Work Permit Details</span>
                </div>
                <div className={`px-4 py-1.5 rounded-xl text-sm font-semibold shadow-sm ${getStatusStyles(workpermit.status)}`}>
                  {getStatusDisplayText(workpermit.status)}
                </div>
              </div>

              <div className="p-4 flex-1 overflow-auto">
                {/* BASIC INFORMATION */}
                <div className="mb-4 bg-steel-50 p-3.5 rounded-xl border border-steel-100">
                  <h3 className="text-xs font-semibold text-graphite-700 mb-2.5 uppercase tracking-wide flex items-center">
                    <div className="p-1.5 bg-brand-100 rounded-md mr-2">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-brand-600">
                        <line x1="4" x2="20" y1="9" y2="9"></line>
                        <line x1="4" x2="20" y1="15" y2="15"></line>
                        <line x1="10" x2="8" y1="3" y2="21"></line>
                        <line x1="16" x2="14" y1="3" y2="21"></line>
                      </svg>
                    </div>
                    Basic Info
                  </h3>
                  <div className="grid grid-cols-2 gap-2.5">
                    <div className="bg-white px-3 py-2.5 rounded-lg shadow-sm border border-steel-100 flex items-center">
                      <div className="w-7 h-7 bg-brand-50 rounded-md flex items-center justify-center mr-2.5 flex-shrink-0">
                        <span className="text-brand-600 font-bold text-sm">#</span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] text-graphite-500 uppercase">Work Permit</p>
                        <p className="font-semibold text-graphite-900 text-sm truncate">{workpermit.work_permit_number}</p>
                      </div>
                    </div>
                    <div className="bg-white px-3 py-2.5 rounded-lg shadow-sm border border-steel-100 flex items-center">
                      <div className="w-7 h-7 bg-blue-50 rounded-md flex items-center justify-center mr-2.5 flex-shrink-0">
                        <FileText className="h-3.5 w-3.5 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] text-graphite-500 uppercase">Order</p>
                        <p className="font-semibold text-graphite-900 text-sm truncate">{workpermit.order_number || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* TURBINE INFORMATION */}
                <div className="mb-4 bg-steel-50 p-3.5 rounded-xl border border-steel-100">
                  <div className="flex items-center justify-between mb-2.5">
                    <h3 className="text-xs font-semibold text-graphite-700 uppercase tracking-wide flex items-center">
                      <div className="p-1.5 bg-blue-100 rounded-md mr-2">
                        <MapPin className="h-3.5 w-3.5 text-blue-600" />
                      </div>
                      Turbine
                    </h3>
                    {workpermit.status === 'signed' && !isEditingTurbine && (
                      <button
                        onClick={() => {
                          setIsEditingTurbine(true);
                          setSelectedTurbineId(workpermit.turbine || null);
                        }}
                        className="text-[11px] px-2.5 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 font-medium"
                      >
                        Edit
                      </button>
                    )}
                  </div>

                  {isEditingTurbine ? (
                    <div className="space-y-4">
                      <div className="bg-white p-3 rounded-xl shadow-sm border border-steel-100">
                        <p className="text-xs text-graphite-500 mb-2">Select Turbine</p>
                        <select
                          value={selectedTurbineId || ''}
                          onChange={(e) => setSelectedTurbineId(e.target.value ? parseInt(e.target.value) : null)}
                          className="w-full p-2 border border-steel-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">-- Select Turbine --</option>
                          {turbines.map((turbine) => (
                            <option key={turbine.id} value={turbine.id}>
                              {turbine.turbine_number} - {turbine.model_data?.name || 'N/A'} ({turbine.location || 'N/A'})
                            </option>
                          ))}
                        </select>
                      </div>

                      {selectedTurbineId && (
                        <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
                          <p className="text-xs text-blue-600 font-medium mb-2">Selected Turbine Preview:</p>
                          {(() => {
                            const selected = turbines.find(t => t.id === selectedTurbineId);
                            if (!selected) return null;
                            return (
                              <div className="text-sm text-graphite-700">
                                <p><strong>Number:</strong> {selected.turbine_number}</p>
                                <p><strong>Model / Type:</strong> {selected.model_data?.name || 'N/A'} / {selected.type_data?.name || 'N/A'}</p>
                                <p><strong>Location:</strong> {selected.location || 'N/A'}</p>
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => {
                            setIsEditingTurbine(false);
                            setSelectedTurbineId(null);
                          }}
                          className="px-4 py-2 text-sm text-graphite-600 bg-steel-200 rounded-full hover:bg-steel-300"
                          disabled={savingTurbine}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveTurbine}
                          disabled={!selectedTurbineId || savingTurbine}
                          className="px-4 py-2 text-sm text-white bg-emerald-600 rounded-full hover:bg-emerald-700 disabled:bg-steel-400 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {savingTurbine ? (
                            <>
                              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Saving...
                            </>
                          ) : 'Save Turbine'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-3 gap-2.5">
                        <div className="bg-white px-3 py-2.5 rounded-lg shadow-sm border border-steel-100 flex items-center">
                          <div className="w-7 h-7 bg-blue-50 rounded-md flex items-center justify-center mr-2.5 flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="3"/>
                              <path d="M12 2v4m0 12v4M2 12h4m12 0h4"/>
                            </svg>
                          </div>
                          <div className="min-w-0">
                            <p className="text-[11px] text-graphite-500 uppercase">Turbine</p>
                            <p className="font-semibold text-graphite-900 text-sm truncate">{workpermit.turbine_data?.turbine_number || 'N/A'}</p>
                          </div>
                        </div>
                        <div className="bg-white px-3 py-2.5 rounded-lg shadow-sm border border-steel-100 flex items-center">
                          <div className="w-7 h-7 bg-purple-50 rounded-md flex items-center justify-center mr-2.5 flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                            </svg>
                          </div>
                          <div className="min-w-0">
                            <p className="text-[11px] text-graphite-500 uppercase">Model/Type</p>
                            <p className="font-semibold text-graphite-900 text-sm truncate">{workpermit.turbine_data?.model_data?.name || 'N/A'}/{workpermit.turbine_data?.type_data?.name || 'N/A'}</p>
                          </div>
                        </div>
                        <div className="bg-white px-3 py-2.5 rounded-lg shadow-sm border border-blue-100 flex items-center">
                          <div className="w-7 h-7 bg-blue-500 rounded-md flex items-center justify-center mr-2.5 flex-shrink-0">
                            <MapPin className="h-3.5 w-3.5 text-white" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-[11px] text-blue-500 uppercase">Location</p>
                            <p className="font-semibold text-graphite-900 text-sm truncate">{workpermit.turbine_data?.location || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* WORK INFORMATION */}
                <div className="bg-steel-50 p-3.5 rounded-xl border border-steel-100">
                  <h3 className="text-xs font-semibold text-graphite-700 mb-2.5 uppercase tracking-wide flex items-center">
                    <div className="p-1.5 bg-emerald-100 rounded-md mr-2">
                      <Briefcase className="h-3.5 w-3.5 text-emerald-600" />
                    </div>
                    Work Details
                  </h3>
                  <div className="space-y-2.5">
                    <div className="bg-white px-3 py-2.5 rounded-lg shadow-sm border border-emerald-100 flex items-center">
                      <div className="w-7 h-7 bg-emerald-500 rounded-md flex items-center justify-center mr-2.5 flex-shrink-0">
                        <Clock className="h-3.5 w-3.5 text-white" />
                      </div>
                      <div className="flex-1 flex items-center gap-2 flex-wrap">
                        <p className="text-[11px] text-emerald-600 uppercase font-medium">Date:</p>
                        <span className="font-semibold text-graphite-900 text-sm">
                          {workpermit.planned_date_start ? new Date(workpermit.planned_date_start).toLocaleString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }).replace(',', '') : 'N/A'}
                        </span>
                        <span className="text-emerald-500 font-bold">→</span>
                        <span className="font-semibold text-graphite-900 text-sm">
                          {workpermit.planned_date_end ? new Date(workpermit.planned_date_end).toLocaleString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }).replace(',', '') : 'N/A'}
                        </span>
                      </div>
                    </div>

                    <div className={`bg-white p-3 rounded-xl shadow-sm border border-steel-100 ${workpermit?.work_scope_data?.name === "Other" ? 'border border-red-500' : ''}`}>
                      {status === 'signed' ? (
                        <>
                          {workpermit?.work_scope_data?.name === "Other" ? (
                            <>
                              <div className="grid grid-cols-1 custom:grid-cols-2 gap-4">
                                <div className="bg-white p-3 rounded-xl shadow-sm border border-steel-100">
                                  <p className="text-xs text-graphite-500 mb-2">Scope of Work (undefined)</p>
                                  <p className="font-medium text-graphite-900 text-sm">{workpermit?.work_scope_real || 'N/A'}</p>
                                </div>
                                <div className="bg-white p-3 rounded-xl shadow-sm border border-steel-100">
  <p className="text-xs text-graphite-500">Assign correct scope of work</p>
  <div className="relative flex items-center space-x-2">
    <select
      className="w-full py-1 pr-8 rounded-full text-sm appearance-none bg-transparent font-medium text-graphite-900 focus:outline-none border border-steel-300"
      value={selectedScopeId}
      onChange={(e) => setSelectedScopeId(e.target.value)}
    >
      <option value="" disabled>Select Scope of Work</option>
      {scopesOfWork
        .filter((scope) => scope.name !== "Other")
        .map((scope) => (
          <option key={scope.id} value={scope.id}>
            {scope.name}
          </option>
        ))}
    </select>
    <div className="flex items-center space-x-2">
      {selectedScopeId ? (
        <>
          <button
            className="px-3 py-1.5 border border-steel-500 bg-white font-medium text-black rounded-full hover:bg-steel-50 flex items-center text-sm"
            onClick={() => setSelectedScopeId("")}
          >
            Cancel
          </button>
          <button
            className="px-3 py-1.5 border border-blue-500 bg-blue-600 font-medium text-white rounded-full hover:bg-blue-700 flex items-center text-sm"
            onClick={handleConfirmScope}
          >
            Confirm
          </button>
        </>
      ) : (
        <button
          className="px-3 py-1.5 border border-blue-500 bg-blue-600 font-medium text-white rounded-full whitespace-nowrap hover:bg-blue-700 flex items-center text-sm"
          onClick={() => setIsModalOpen(true)}
        >
          Add new
        </button>
      )}
    </div>
  </div>
</div>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="mb-1">
                                <p className="text-xs text-graphite-500">Scope of Work</p>
                              </div>
                              {isEditingScope ? (
                                <div className="flex items-center justify-between">
  <div className="relative">
    <select
      className="w-auto py-1 pr-8 rounded-full text-sm appearance-none bg-transparent font-medium text-graphite-900 focus:outline-none border border-steel-300"
      value={selectedScopeId}
      onChange={(e) => setSelectedScopeId(e.target.value)}
    >
      <option value="" disabled>Select Scope of Work</option>
      {scopesOfWork
        .filter((scope) => scope.name !== "Other")
        .map((scope) => (
          <option key={scope.id} value={scope.id}>
            {scope.name}
          </option>
        ))}
    </select>
  </div>
  <div className="flex space-x-2">
    {selectedScopeId ? (
      <>
        <button
          className="px-3 py-1.5 border border-steel-300 text-graphite-700 font-medium rounded-full hover:bg-steel-50 flex items-center text-sm whitespace-nowrap"
          onClick={() => setSelectedScopeId("")}
        >
          Cancel
        </button>
        <button
          className="px-3 py-1.5 border border-blue-500 bg-blue-600 font-medium text-white rounded-full hover:bg-blue-700 flex items-center text-sm whitespace-nowrap"
          onClick={handleConfirmScope}
        >
          Confirm
        </button>
      </>
    ) : (
      <>
        <button
          className="px-3 py-1.5 border border-steel-300 text-graphite-700 font-medium rounded-full hover:bg-steel-50 flex items-center text-sm whitespace-nowrap"
          onClick={handleCancelEdit}
        >
          Cancel
        </button>
        <button
          className="px-3 py-1.5 border border-blue-500 bg-blue-600 font-medium text-white rounded-full hover:bg-blue-700 flex items-center text-sm whitespace-nowrap"
          onClick={() => setIsModalOpen(true)}
        >
          Add new
        </button>
      </>
    )}
  </div>
</div>
                              ) : (
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <div className="w-7 h-7 bg-amber-50 rounded-md flex items-center justify-center mr-2.5">
                                      <Briefcase className="h-3.5 w-3.5 text-amber-600" />
                                    </div>
                                    <div>
                                      <p className="text-[11px] text-graphite-500 uppercase">Scope</p>
                                      <p className="font-semibold text-graphite-900 text-sm">{workpermit?.work_scope_data?.name || 'N/A'}</p>
                                    </div>
                                  </div>
                                  <button
                                    onClick={handleChangeScope}
                                    className="px-2.5 py-1 text-[11px] bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 font-medium"
                                  >
                                    Change
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        <div className="flex items-center">
                          <div className="w-7 h-7 bg-amber-50 rounded-md flex items-center justify-center mr-2.5">
                            <Briefcase className="h-3.5 w-3.5 text-amber-600" />
                          </div>
                          <div>
                            <p className="text-[11px] text-graphite-500 uppercase">Scope</p>
                            <p className="font-semibold text-graphite-900 text-sm">{workpermit?.work_scope_data?.name || 'N/A'}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="bg-white px-3 py-2.5 rounded-lg shadow-sm border border-steel-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1 min-w-0 mr-2">
                          <div className="w-7 h-7 bg-graphite-100 rounded-md flex items-center justify-center mr-2.5 flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-graphite-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                              <polyline points="14,2 14,8 20,8"/>
                            </svg>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] text-graphite-500 uppercase">Description</p>
                            <p className="font-medium text-graphite-700 text-sm truncate">
                              {workpermit?.comment || 'No description available'}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setIsCommentExpanded(!isCommentExpanded)}
                          className="flex-shrink-0 px-2.5 py-1 text-[11px] bg-steel-100 text-graphite-600 rounded-md hover:bg-steel-200 font-medium"
                        >
                          {isCommentExpanded ? 'Less' : 'More'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Team Members Panel */}
            <div className={`${columnWidths.middleCol} bg-white rounded-2xl border border-steel-200 overflow-auto transition-all duration-300 shadow-sm`}>
              {isCommentExpanded ? (
                <></> // Pusty komponent, gdy isCommentExpanded jest true
              ) : (
                <>
                  <div className="w-full flex items-center justify-between p-4 text-left bg-gradient-to-r from-amber-50 to-white border-b border-amber-200">
                    <div className="flex items-center">
                      <div className="p-2 bg-amber-500 rounded-lg mr-3">
                        <Users className="h-5 w-5 text-white" />
                      </div>
                      <span className="text-lg font-semibold text-graphite-900">Team Members</span>
                    </div>
                  </div>

                  <div className="divide-y divide-steel-200">
                    {workpermit.team_data?.users
                      ?.slice()
                      .sort((a, b) => {
                        const roleA = getUserRole(a.id);
                        const roleB = getUserRole(b.id);
                        const rolePriority = {
                          Supervisor: 1,
                          Preparer: 2,
                          'Team Member': 3,
                        };
                        return rolePriority[roleA] - rolePriority[roleB];
                      })
                      .map((user) => (
                        <div key={user.id} className="p-4">
                          <button
                            onClick={() => toggleUserExpansion(user.id)}
                            className="w-full text-left"
                          >
                            <div className="flex items-center">
                              <div className="mr-3">
                                <div className="w-12 h-12 bg-steel-50 rounded-full flex items-center justify-center border-2 border-steel-200 overflow-hidden">
                                  <img
                                    alt={`${user.first_name || ''} ${user.last_name || ''}`}
                                    className="w-full h-full object-cover"
                                    src={user.profile_picture || '/api/placeholder/96/96'}
                                  />
                                </div>
                              </div>
                              <div className="flex-1">
                                <h3 className="font-medium text-graphite-900">{`${user.first_name || ''} ${user.last_name || ''}`}</h3>
                                <div className="flex items-center flex-wrap gap-2">
                                  <span className="text-sm text-graphite-500 bg-steel-100 px-2 py-0.5 rounded-full">{getUserRole(user.id)}</span>
                                  {teamMemberQualifications[user.id] && !teamMemberQualifications[user.id].hasValidNomination && (
                                    <div className="flex items-center bg-red-100 px-2 py-0.5 rounded-full">
                                      <AlertCircle className="h-3.5 w-3.5 text-red-600 mr-1" />
                                      <span className="text-xs text-red-600 font-semibold">No valid nomination</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              {userExpanded === user.id ? (
                                <ChevronDown className="h-5 w-5 text-graphite-500 transition-transform rotate-180" />
                              ) : (
                                <ChevronDown className="h-5 w-5 text-graphite-500 transition-transform" />
                              )}
                            </div>
                          </button>

                          {userExpanded === user.id && (
                            <div className="mt-3 pl-14">
                              <div className="grid grid-cols-1 gap-2 text-sm mb-4">
                                <div>
                                  <p className="text-graphite-500">Email</p>
                                  <p className="text-graphite-900">{user.email || 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-graphite-500">Phone</p>
                                  <p className="text-graphite-900">{user.phone_number || 'N/A'}</p>
                                </div>
                              </div>

                              {/* Nominations section */}
                              <div className="mt-4 border-t border-steel-100 pt-3">
    <p className="text-sm font-medium text-graphite-900 mb-2">Nominations valid for this workpermit:</p>
    {relevantNominations.length > 0 ? (
      relevantNominations.map(({ nomination, matchingWorkScopes }) => (
        <button
          key={nomination.id}
          onClick={(event) => handleNominationClick(nomination, matchingWorkScopes, event)}
          className="w-full text-left p-2 border border-green-500 rounded-md bg-green-50 text-xs mb-2 cursor-pointer hover:bg-green-200 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="font-medium">{nomination.nomination_number || 'N/A'}</p>
              <div className="mt-1 text-graphite-600">
                Valid:{' '}
                {nomination.date_start && nomination.date_end
                  ? `${new Date(nomination.date_start).toLocaleDateString('en-GB')} - ${new Date(nomination.date_end).toLocaleDateString('en-GB')}`
                  : 'N/A'}
              </div>
              <div className="mt-2">
                {matchingWorkScopes.length > 0 ? (
                  <ul className="list-none text-graphite-600 font-semibold">
                    {matchingWorkScopes.map((scope) => (
                      <li key={scope.id} className="mt-1">
                        {scope.name || 'N/A'}
                        {scope.categories && scope.categories.length > 0 ? (
                          <ul className="list-none ml-4 text-graphite-500 font-semibold">
                            {scope.categories.map((category) => (
                              <li key={category.id}>
                                Platform: {category.name || 'N/A'}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="ml-4 text-graphite-500 font-semibold">All platforms</div>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-graphite-600 italic">No matching work scopes</p>
                )}
              </div>
            </div>
            <div className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs">
              {nomination.status || 'N/A'}
            </div>
          </div>
        </button>
      ))
    ) : (
      <p className="text-sm text-graphite-500 italic">No relevant nominations for this work permit.</p>
    )}
  </div>
  {/* Dodanie ECCNominationApprovalModal */}
      <ECCNominationApprovalModal
        isOpen={isNominationModalOpen}
        onClose={handleCloseNominationModal}
        nomination={selectedNomination}
        refreshNominations={refreshNominations}
      />
                            </div>
                          )}
                        </div>
                      ))}

                    {(!workpermit.team_data?.users || workpermit.team_data?.users.length === 0) && (
                      <div className="p-4 text-center text-graphite-500">
                        No team members assigned to this work permit.
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Document Preview Panel */}
            <div className={`${columnWidths.rightCol} bg-white rounded-2xl border border-steel-200 transition-all duration-300 flex flex-col`}>
              {isCommentExpanded ? (
                <>
                  <div className="w-full flex items-center justify-between p-4 text-left border-b border-steel-200">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-graphite-900" />
                      <span className="text-lg font-medium text-graphite-900">Work Permit Description</span>
                    </div>
                    <button
                      onClick={() => setIsCommentExpanded(false)}
                      className="p-2 rounded-full text-graphite-600 hover:bg-steel-100"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M6 12h12" />
                      </svg>
                    </button>
                  </div>
                  <div className="flex-1 p-4 overflow-auto">
                    <p className="text-graphite-900 text-sm">
                      {workpermit?.comment || 'No description available'}
                    </p>
                  </div>
                </>
              ) : previewExpanded ? (
                <>
                  <div className="w-full flex items-center justify-between p-4 text-left border-b border-steel-200">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-graphite-900" />
                      <span className="text-lg font-medium text-graphite-900">Instruction Preview</span>
                    </div>
                    <button
                      onClick={() => {
                        setPreviewExpanded(false);
                        if (previewUrl) {
                          URL.revokeObjectURL(previewUrl); // Zwolnienie pamięci
                          setPreviewUrl(null);
                        }
                      }}
                      className="p-2 rounded-full text-graphite-600 hover:bg-steel-100"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M6 12h12" />
                      </svg>
                    </button>
                  </div>
                  <div className="flex-1 p-4 overflow-auto">
                    {previewUrl ? (
                      <div className="flex flex-col items-center">
                        <p className="text-sm text-graphite-600 mb-2">Attachment: {getFileNameFromUrl(workpermit.attachments)}</p>
                        <iframe
                          src={previewUrl}
                          title="Attachment Preview"
                          className="w-full h-[600px] mt-4 border border-steel-300"
                        />
                      </div>
                    ) : (
                      <p className="text-graphite-500 text-center">No attachment available or failed to load</p>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <button
                    onClick={() => setPreviewExpanded(true)}
                    className="p-2 rounded-full text-graphite-600 hover:bg-steel-100"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 12h12" />
                      <path d="M12 6v12" />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            {/* ModalAddScope */}
            <ModalAddScope
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onSave={() => {
                fetchScopesOfWork();
                setIsModalOpen(false);
              }}
              isEmbedded={false}
            />
          </div>
        </div>

        {/* Footer with actions */}
        <footer className="bg-white border-t-2 border-brand-500 px-6 py-4 flex justify-between items-center shadow-lg">
          <div className="flex items-center">
            {workpermit.status === 'signed' && (
              <div className="flex items-center bg-steel-50 px-4 py-2 rounded-xl">
                <Calendar className="h-5 w-5 mr-2 text-brand-500" />
                <span className="text-sm text-graphite-600">
                  Submitted: <strong className="text-graphite-900">{workpermit.sign_date ? formatDateTime(workpermit.sign_date) : 'N/A'}</strong>
                </span>
              </div>
            )}
            {workpermit.status === 'rejected' && (
              <div className="flex items-center bg-red-50 px-4 py-2 rounded-xl">
                <AlertCircle className="h-5 w-5 mr-2 text-red-500" />
                <span className="text-sm text-graphite-600">
                  Rejected by <strong className="text-graphite-900">{workpermit.approver_name || 'N/A'}</strong> on <strong className="text-graphite-900">{workpermit.rejected_date ? formatDateTime(workpermit.rejected_date) : 'N/A'}</strong>
                </span>
              </div>
            )}
            {workpermit.status === 'approved' && (
              <div className="flex items-center bg-emerald-50 px-4 py-2 rounded-xl">
                <svg className="h-5 w-5 mr-2 text-emerald-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-graphite-600">
                  Approved by <strong className="text-graphite-900">{workpermit.approver_name || 'N/A'}</strong> on <strong className="text-graphite-900">{workpermit.approve_date ? formatDateTime(workpermit.approve_date) : 'N/A'}</strong>
                </span>
              </div>
            )}
          </div>

          <div className="flex space-x-3">
            {workpermit.status === 'signed' ? (
              <>
                <Button
                  onClick={() => setShowRejectModal(true)}
                  className="px-5 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium shadow-sm transition-all hover:shadow-md"
                >
                  Reject
                </Button>
                <Button
                  onClick={handleCheckInstruction}
                  className="px-6 py-2.5 bg-brand-500 text-white rounded-xl hover:bg-brand-600 font-medium shadow-sm transition-all hover:shadow-md disabled:bg-steel-300 disabled:text-graphite-500"
                  disabled={workpermit?.work_scope_data?.name === "Other"}
                >
                  Continue to Instructions →
                </Button>
              </>
            ) : workpermit.status === 'approved' ? (
              <>
                <button
                  onClick={handleClose}
                  className="px-5 py-2.5 font-medium bg-white text-graphite-600 border border-steel-300 rounded-xl hover:bg-steel-50 transition-colors"
                >
                  Close
                </button>
                <Button
                  onClick={() => setShowRevertModal(true)}
                  className="px-5 py-2.5 bg-brand-500 text-white rounded-xl hover:bg-brand-600 font-medium shadow-sm"
                >
                  Revert to Pending
                </Button>
                <Button
                  onClick={handleCheckInstruction}
                  className="px-6 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-medium shadow-sm"
                  disabled={workpermit?.work_scope_data?.name === "Other"}
                >
                  Edit / Re-approve
                </Button>
              </>
            ) : (
              <button
                onClick={handleClose}
                className="px-6 py-2.5 mr-8 font-medium bg-white text-graphite-600 border border-steel-300 rounded-xl hover:bg-steel-50 transition-colors"
              >
                Close
              </button>
            )}
          </div>
        </footer>

        {/* Reject Modal */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white w-full max-w-md p-6 rounded-lg shadow-lg">
              <h2 className="text-xl font-bold mb-4">Reject Workpermit</h2>
              <p className="text-graphite-600 mb-4">Please provide a reason for rejection:</p>

              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter rejection reason..."
                className="w-full p-3 border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                rows={4}
              />

              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectReason('');
                  }}
                  className="px-4 py-2 rounded-full hover:bg-steel-100 transition-colors"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReject}
                  className="px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700"
                  disabled={!rejectReason.trim()}
                >
                  Confirm Rejection
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Revert to Pending Modal */}
        {showRevertModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white w-full max-w-md p-6 rounded-lg shadow-lg">
              <h2 className="text-xl font-bold mb-4">Revert to Pending</h2>
              <p className="text-graphite-600 mb-4">
                Are you sure you want to revert this work permit to pending status?
                This will clear the approval and allow re-editing.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-amber-800 text-sm">
                  <strong>Note:</strong> The approver, approval date, and instruction selection will be cleared.
                </p>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowRevertModal(false)}
                  className="px-4 py-2 rounded-full hover:bg-steel-100 transition-colors"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    handleRevertToPending();
                    setShowRevertModal(false);
                  }}
                  className="px-4 py-2 bg-brand-500 text-white rounded-full hover:bg-brand-600"
                >
                  Confirm Revert
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Isolation Flow Modal */}
        {showIsolationFlowModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="px-6 py-4 border-b border-steel-200 flex items-center justify-between bg-brand-50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-brand-500 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-graphite-800">Isolation Flow Submissions</h2>
                    <p className="text-sm text-graphite-500">Work Permit: {workpermit.work_permit_number}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowIsolationFlowModal(false)}
                  className="p-2 hover:bg-steel-100 rounded-full transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-auto p-6">
                {loadingIsolationFlow ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-brand-500"></div>
                    <span className="ml-3 text-graphite-600">Loading submissions...</span>
                  </div>
                ) : isolationFlowData && isolationFlowData.length > 0 ? (
                  <div className="space-y-6">
                    {isolationFlowData.map((submission, index) => (
                      <div key={submission.id} className="border border-steel-200 rounded-xl overflow-hidden">
                        {/* Submission Header */}
                        <div className="bg-steel-50 px-4 py-3 border-b border-steel-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="px-2 py-1 bg-brand-100 text-brand-700 text-xs font-medium rounded">
                                Submission #{index + 1}
                              </span>
                              <span className={`px-2 py-1 text-xs font-medium rounded ${
                                submission.status === 'reviewed' ? 'bg-green-100 text-green-700' :
                                submission.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
                                'bg-steel-100 text-graphite-700'
                              }`}>
                                {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => {
                                  const token = localStorage.getItem('access_token');
                                  fetch(`${API_BASE_URL}/isolation-flow/${submission.id}/download/`, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  })
                                    .then(response => {
                                      if (!response.ok) throw new Error('Failed to download PDF');
                                      return response.blob();
                                    })
                                    .then(blob => {
                                      const url = window.URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = `isolation_flow_${workpermit.work_permit_number}_${submission.id}.pdf`;
                                      document.body.appendChild(a);
                                      a.click();
                                      window.URL.revokeObjectURL(url);
                                      document.body.removeChild(a);
                                    })
                                    .catch(err => {
                                      console.error('Error downloading PDF:', err);
                                      alert('Error downloading PDF');
                                    });
                                }}
                                className="flex items-center gap-1 px-3 py-1.5 bg-brand-500 text-white text-xs font-medium rounded-lg hover:bg-brand-600 transition-colors"
                                title="Download PDF"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                  <polyline points="7 10 12 15 17 10"></polyline>
                                  <line x1="12" y1="15" x2="12" y2="3"></line>
                                </svg>
                                PDF
                              </button>
                              <span className="text-sm text-graphite-500">
                                {new Date(submission.submitted_at).toLocaleString()}
                              </span>
                            </div>
                          </div>
                          <div className="mt-2 text-sm text-graphite-600">
                            Submitted by: <span className="font-medium">{submission.submitted_by_name}</span>
                          </div>
                        </div>

                        {/* Submission Content - Table Format like PDF */}
                        <div className="p-4">
                          {submission.instruction_snapshot?.data?.sections?.map((section, sectionIndex) => (
                            <div key={sectionIndex} className="mb-6 last:mb-0">
                              {/* Section Title */}
                              <div className="bg-blue-100 px-3 py-2 rounded-t-lg border border-blue-200">
                                <h4 className="font-semibold text-graphite-800 flex items-center gap-2">
                                  <span className="w-6 h-6 bg-brand-500 text-white text-xs rounded flex items-center justify-center">
                                    {section.mark || sectionIndex + 1}
                                  </span>
                                  {section.title}
                                </h4>
                              </div>

                              {/* Table Header */}
                              <div className="border border-t-0 border-steel-300 overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="bg-steel-100">
                                      <th className="border-r border-steel-300 px-2 py-2 text-left font-semibold w-12">Seq</th>
                                      <th className="border-r border-steel-300 px-2 py-2 text-left font-semibold">Isolation ID / Description</th>
                                      <th className="border-r border-steel-300 px-2 py-2 text-left font-semibold w-24">Energy Type</th>
                                      <th className="border-r border-steel-300 px-2 py-2 text-left font-semibold w-24">Isolation Pos.</th>
                                      <th className="border-r border-steel-300 px-2 py-2 text-left font-semibold w-28">Verification</th>
                                      <th className="border-r border-steel-300 px-2 py-2 text-left font-semibold w-24">Location</th>
                                      <th className="border-r border-steel-300 px-2 py-2 text-left font-semibold w-24">Dispersion</th>
                                      <th className="border-r border-steel-300 px-2 py-2 text-center font-semibold w-16">Photo</th>
                                      <th className="px-2 py-2 text-center font-semibold w-16">Done</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {section.steps?.map((step, stepIndex) => {
                                      const stepId = `${section.mark || section.title}${stepIndex + 1}`;
                                      const isCompleted = submission.completed_steps?.[submission.instruction_snapshot?.id?.toString()]?.[stepId];
                                      const stepImages = submission.step_images?.filter(img => img.step_id === stepId) || [];

                                      return (
                                        <tr key={stepIndex} className={`border-t border-steel-300 ${isCompleted ? 'bg-green-50' : ''}`}>
                                          <td className="border-r border-steel-300 px-2 py-2 font-medium text-center">
                                            {section.mark}{stepIndex + 1}
                                          </td>
                                          <td className="border-r border-steel-300 px-2 py-2">
                                            {step.field1 || '-'}
                                          </td>
                                          <td className="border-r border-steel-300 px-2 py-2">
                                            {step.field2 || '-'}
                                          </td>
                                          <td className="border-r border-steel-300 px-2 py-2">
                                            {step.field3 || '-'}
                                          </td>
                                          <td className="border-r border-steel-300 px-2 py-2">
                                            {step.field4 || '-'}
                                          </td>
                                          <td className="border-r border-steel-300 px-2 py-2">
                                            {step.field5 || '-'}
                                          </td>
                                          <td className="border-r border-steel-300 px-2 py-2">
                                            {step.field6 || '-'}
                                          </td>
                                          <td className="border-r border-steel-300 px-2 py-2 text-center">
                                            {stepImages.length > 0 ? (
                                              <div className="flex flex-wrap gap-1 justify-center">
                                                {stepImages.map((img, imgIdx) => (
                                                  <div key={imgIdx} className="relative">
                                                    <img
                                                      src={img.image_url || img.image}
                                                      alt={`Step ${stepId} - ${imgIdx + 1}`}
                                                      className="w-8 h-8 object-cover rounded border border-steel-200 cursor-pointer hover:opacity-80 transition-opacity"
                                                      onClick={() => window.open(img.image_url || img.image, '_blank')}
                                                    />
                                                    {stepImages.length > 1 && (
                                                      <span className="absolute -bottom-1 -right-1 bg-blue-600 text-white text-[8px] w-3 h-3 rounded-full flex items-center justify-center">
                                                        {imgIdx + 1}
                                                      </span>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            ) : (
                                              <span className="text-graphite-400">-</span>
                                            )}
                                          </td>
                                          <td className="px-2 py-2 text-center">
                                            {isCompleted ? (
                                              <div className="w-6 h-6 bg-green-500 rounded flex items-center justify-center mx-auto">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                  <polyline points="20 6 9 17 4 12"></polyline>
                                                </svg>
                                              </div>
                                            ) : (
                                              <div className="w-6 h-6 bg-steel-300 rounded mx-auto"></div>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>

                              {/* Section Signature */}
                              {(() => {
                                // Try multiple possible keys for section signature
                                const sectionKey = section.mark || section.title || '';
                                const sigData = submission.section_signatures?.[sectionKey];

                                console.log(`[Signature] Section key: "${sectionKey}", sigData type: ${typeof sigData}`);

                                if (!sigData) return null;

                                let signatureImage = null;
                                let signedAt = null;

                                // Handle different data formats
                                if (typeof sigData === 'string') {
                                  console.log(`[Signature] String data, first 50 chars: ${sigData.substring(0, 50)}`);
                                  // Check if it starts with data: (already base64 image)
                                  if (sigData.startsWith('data:image')) {
                                    signatureImage = sigData;
                                  } else if (sigData.startsWith('{')) {
                                    // Looks like JSON
                                    try {
                                      const parsed = JSON.parse(sigData);
                                      signatureImage = parsed.signature || null;
                                      signedAt = parsed.signed_at || null;
                                      console.log(`[Signature] Parsed JSON, has signature: ${!!signatureImage}`);
                                    } catch (e) {
                                      console.error('[Signature] Failed to parse JSON:', e);
                                      signatureImage = sigData;
                                    }
                                  } else {
                                    // Plain base64 or other string
                                    signatureImage = sigData;
                                  }
                                } else if (typeof sigData === 'object' && sigData !== null) {
                                  // Already an object
                                  console.log(`[Signature] Object data, keys: ${Object.keys(sigData).join(', ')}`);
                                  signatureImage = sigData.signature || null;
                                  signedAt = sigData.signed_at || null;
                                }

                                console.log(`[Signature] Final signatureImage starts with: ${signatureImage?.substring(0, 30)}`);

                                if (!signatureImage) return null;

                                // Ensure signature has proper data URI prefix
                                if (signatureImage && !signatureImage.startsWith('data:')) {
                                  console.log('[Signature] Adding data:image prefix');
                                  signatureImage = `data:image/png;base64,${signatureImage}`;
                                }

                                return (
                                  <div className="border border-t-0 border-steel-300 bg-blue-50 px-3 py-2 rounded-b-lg">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <span className="text-xs font-medium text-blue-700">Section Signature</span>
                                        {signedAt && (
                                          <p className="text-xs text-blue-600">
                                            {new Date(signedAt).toLocaleString()}
                                          </p>
                                        )}
                                      </div>
                                      <img
                                        src={signatureImage}
                                        alt="Signature"
                                        className="max-h-12 border border-blue-200 rounded bg-white"
                                        onError={(e) => {
                                          console.error('[Signature] Image failed to load, src was:', e.target.src?.substring(0, 50));
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto bg-steel-100 rounded-full flex items-center justify-center mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-graphite-400">
                        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-graphite-800 mb-2">No Submissions Yet</h3>
                    <p className="text-graphite-500">No isolation flow submissions have been received for this work permit.</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-steel-200 bg-steel-50">
                <div className="flex justify-end">
                  <Button
                    onClick={() => setShowIsolationFlowModal(false)}
                    className="px-6 py-2 rounded-full"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Renderowanie dla ekranu "instruction" (dla statusu 'signed' lub gdy użytkownik chce re-edytować approved)
  if (activeTab === 'instruction' && (workpermit.status === 'signed' || workpermit.status === 'approved')) {
    return (
      <div className="flex flex-col h-screen">
        {/* Header */}
        <header className="bg-white border-b border-steel-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => setActiveTab('details')}
                className="p-2 rounded-full text-graphite-600 hover:bg-steel-100 mr-3"
                aria-label="Go back"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-xl font-bold text-graphite-900">
                Workpermit <span className="text-base font-normal">({workpermit.work_permit_number || 'N/A'})</span>
              </h1>
            </div>

            <div className="flex items-center">
              <button
                className="flex items-center focus:outline-none"
                onClick={() => setActiveTab('details')}
              >
                <div className="rounded-full p-1 bg-blue-100 text-blue-600">
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="ml-2 text-sm font-medium text-blue-600">Review Details</span>
              </button>

              <ChevronRight className="h-4 w-4 mx-3 text-graphite-400" />

              <button
                className="flex items-center focus:outline-none"
              >
                <div className="rounded-full p-1 bg-blue-100 text-blue-600">
                  <FileText className="h-5 w-5" />
                </div>
                <span className="ml-2 text-sm font-medium text-blue-600">Accept Instructions</span>
              </button>

              <ChevronRight className="h-4 w-4 mx-3 text-graphite-400" />

              <div className="flex items-center">
                <div className="rounded-full p-1 bg-steel-100 text-graphite-600">
                  <AlertCircle className="h-5 w-5" />
                </div>
                <span className="ml-2 text-sm font-medium text-graphite-600">Confirm Workpermit</span>
              </div>
            </div>
          </div>
        </header>

        {/* PDF Viewer */}
        <div className="flex-1 overflow-hidden">
          <ECCWorkPermitPDFViewer
            workpermit={updatedWorkPermit || workpermit}
            instructions={instructions}
            isOpen={true}
            onClose={() => setActiveTab('details')}
            onPdfGenerated={handlePdfGenerated}
          />
        </div>

        {/* Footer with actions */}
        <footer className="bg-white border-t border-steel-200 px-4 py-3 flex justify-between items-center">
          <div className="flex items-center text-sm text-graphite-500">
            <Calendar className="h-5 w-5 mr-2 text-graphite-400" />
            <span>Created: <strong>{workpermit.sign_date ? formatDateTime(workpermit.sign_date) : 'N/A'}</strong></span>
          </div>

          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={() => setActiveTab('details')}
              className="px-4 py-2 rounded-full border border-steel-300 text-graphite-700 hover:bg-steel-50"
            >
              Back
            </Button>

            <Button
              onClick={() => setShowRejectModal(true)}
              className="px-4 py-2 rounded-full bg-red-600 text-white hover:bg-red-700"
            >
              Reject
            </Button>

            {instructions.length === 0 ? (
              <Button
                onClick={handleWriteInstruction}
                className="px-6 py-2 rounded-full bg-yellow-600 text-white hover:bg-yellow-700"
              >
                Write Instructions
              </Button>
            ) : !instApproved ? (
              <Button
                onClick={handleConfirmInstruction}
                className="px-6 py-2 rounded-full bg-blue-600 text-white hover:bg-blue-700"
                disabled={!pdfBlob}
              >
                Confirm Instructions
              </Button>
            ) : (
              <Button
                onClick={handleApprove}
                className="px-6 py-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
                disabled={!pdfBlob}
              >
                {workpermit.status === 'approved' ? 'Re-approve Workpermit' : 'Approve Workpermit'}
              </Button>
            )}
          </div>
        </footer>

        {/* Reject Modal */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white w-full max-w-md p-6 rounded-lg shadow-lg">
              <h2 className="text-xl font-bold mb-4">Reject Workpermit</h2>
              <p className="text-graphite-600 mb-4">Please provide a reason for rejection:</p>

              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter rejection reason..."
                className="w-full p-3 border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                rows={4}
              />

              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectReason('');
                  }}
                  className="px-4 py-2 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReject}
                  className="px-4 py-2 rounded-full bg-red-600 text-white hover:bg-red-700"
                  disabled={!rejectReason.trim()}
                >
                  Confirm Rejection
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Isolation Flow Modal */}
        {showIsolationFlowModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="px-6 py-4 border-b border-steel-200 flex items-center justify-between bg-brand-50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-brand-500 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-graphite-800">Isolation Flow Submissions</h2>
                    <p className="text-sm text-graphite-500">Work Permit: {workpermit.work_permit_number}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowIsolationFlowModal(false)}
                  className="p-2 hover:bg-steel-100 rounded-full transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-auto p-6">
                {loadingIsolationFlow ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-brand-500"></div>
                    <span className="ml-3 text-graphite-600">Loading submissions...</span>
                  </div>
                ) : isolationFlowData && isolationFlowData.length > 0 ? (
                  <div className="space-y-6">
                    {isolationFlowData.map((submission, index) => (
                      <div key={submission.id} className="border border-steel-200 rounded-xl overflow-hidden">
                        {/* Submission Header */}
                        <div className="bg-steel-50 px-4 py-3 border-b border-steel-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="px-2 py-1 bg-brand-100 text-brand-700 text-xs font-medium rounded">
                                Submission #{index + 1}
                              </span>
                              <span className={`px-2 py-1 text-xs font-medium rounded ${
                                submission.status === 'reviewed' ? 'bg-green-100 text-green-700' :
                                submission.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
                                'bg-steel-100 text-graphite-700'
                              }`}>
                                {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => {
                                  const token = localStorage.getItem('access_token');
                                  fetch(`${API_BASE_URL}/isolation-flow/${submission.id}/download/`, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  })
                                    .then(response => {
                                      if (!response.ok) throw new Error('Failed to download PDF');
                                      return response.blob();
                                    })
                                    .then(blob => {
                                      const url = window.URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = `isolation_flow_${workpermit.work_permit_number}_${submission.id}.pdf`;
                                      document.body.appendChild(a);
                                      a.click();
                                      window.URL.revokeObjectURL(url);
                                      document.body.removeChild(a);
                                    })
                                    .catch(err => {
                                      console.error('Error downloading PDF:', err);
                                      alert('Error downloading PDF');
                                    });
                                }}
                                className="flex items-center gap-1 px-3 py-1.5 bg-brand-500 text-white text-xs font-medium rounded-lg hover:bg-brand-600 transition-colors"
                                title="Download PDF"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                  <polyline points="7 10 12 15 17 10"></polyline>
                                  <line x1="12" y1="15" x2="12" y2="3"></line>
                                </svg>
                                PDF
                              </button>
                              <span className="text-sm text-graphite-500">
                                {new Date(submission.submitted_at).toLocaleString()}
                              </span>
                            </div>
                          </div>
                          <div className="mt-2 text-sm text-graphite-600">
                            Submitted by: <span className="font-medium">{submission.submitted_by_name}</span>
                          </div>
                        </div>

                        {/* Submission Content - Table Format like PDF */}
                        <div className="p-4">
                          {submission.instruction_snapshot?.data?.sections?.map((section, sectionIndex) => (
                            <div key={sectionIndex} className="mb-6 last:mb-0">
                              {/* Section Title */}
                              <div className="bg-blue-100 px-3 py-2 rounded-t-lg border border-blue-200">
                                <h4 className="font-semibold text-graphite-800 flex items-center gap-2">
                                  <span className="w-6 h-6 bg-brand-500 text-white text-xs rounded flex items-center justify-center">
                                    {section.mark || sectionIndex + 1}
                                  </span>
                                  {section.title}
                                </h4>
                              </div>

                              {/* Table Header */}
                              <div className="border border-t-0 border-steel-300 overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="bg-steel-100">
                                      <th className="border-r border-steel-300 px-2 py-2 text-left font-semibold w-12">Seq</th>
                                      <th className="border-r border-steel-300 px-2 py-2 text-left font-semibold">Isolation ID / Description</th>
                                      <th className="border-r border-steel-300 px-2 py-2 text-left font-semibold w-24">Energy Type</th>
                                      <th className="border-r border-steel-300 px-2 py-2 text-left font-semibold w-24">Isolation Pos.</th>
                                      <th className="border-r border-steel-300 px-2 py-2 text-left font-semibold w-28">Verification</th>
                                      <th className="border-r border-steel-300 px-2 py-2 text-left font-semibold w-24">Location</th>
                                      <th className="border-r border-steel-300 px-2 py-2 text-left font-semibold w-24">Dispersion</th>
                                      <th className="border-r border-steel-300 px-2 py-2 text-center font-semibold w-16">Photo</th>
                                      <th className="px-2 py-2 text-center font-semibold w-16">Done</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {section.steps?.map((step, stepIndex) => {
                                      const stepId = `${section.mark || section.title}${stepIndex + 1}`;
                                      const isCompleted = submission.completed_steps?.[submission.instruction_snapshot?.id?.toString()]?.[stepId];
                                      const stepImages = submission.step_images?.filter(img => img.step_id === stepId) || [];

                                      return (
                                        <tr key={stepIndex} className={`border-t border-steel-300 ${isCompleted ? 'bg-green-50' : ''}`}>
                                          <td className="border-r border-steel-300 px-2 py-2 font-medium text-center">
                                            {section.mark}{stepIndex + 1}
                                          </td>
                                          <td className="border-r border-steel-300 px-2 py-2">
                                            {step.field1 || '-'}
                                          </td>
                                          <td className="border-r border-steel-300 px-2 py-2">
                                            {step.field2 || '-'}
                                          </td>
                                          <td className="border-r border-steel-300 px-2 py-2">
                                            {step.field3 || '-'}
                                          </td>
                                          <td className="border-r border-steel-300 px-2 py-2">
                                            {step.field4 || '-'}
                                          </td>
                                          <td className="border-r border-steel-300 px-2 py-2">
                                            {step.field5 || '-'}
                                          </td>
                                          <td className="border-r border-steel-300 px-2 py-2">
                                            {step.field6 || '-'}
                                          </td>
                                          <td className="border-r border-steel-300 px-2 py-2 text-center">
                                            {stepImages.length > 0 ? (
                                              <div className="flex flex-wrap gap-1 justify-center">
                                                {stepImages.map((img, imgIdx) => (
                                                  <div key={imgIdx} className="relative">
                                                    <img
                                                      src={img.image_url || img.image}
                                                      alt={`Step ${stepId} - ${imgIdx + 1}`}
                                                      className="w-8 h-8 object-cover rounded border border-steel-200 cursor-pointer hover:opacity-80 transition-opacity"
                                                      onClick={() => window.open(img.image_url || img.image, '_blank')}
                                                    />
                                                    {stepImages.length > 1 && (
                                                      <span className="absolute -bottom-1 -right-1 bg-blue-600 text-white text-[8px] w-3 h-3 rounded-full flex items-center justify-center">
                                                        {imgIdx + 1}
                                                      </span>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            ) : (
                                              <span className="text-graphite-400">-</span>
                                            )}
                                          </td>
                                          <td className="px-2 py-2 text-center">
                                            {isCompleted ? (
                                              <div className="w-6 h-6 bg-green-500 rounded flex items-center justify-center mx-auto">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                  <polyline points="20 6 9 17 4 12"></polyline>
                                                </svg>
                                              </div>
                                            ) : (
                                              <div className="w-6 h-6 bg-steel-300 rounded mx-auto"></div>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>

                              {/* Section Signature */}
                              {(() => {
                                // Try multiple possible keys for section signature
                                const sectionKey = section.mark || section.title || '';
                                const sigData = submission.section_signatures?.[sectionKey];

                                console.log(`[Signature] Section key: "${sectionKey}", sigData type: ${typeof sigData}`);

                                if (!sigData) return null;

                                let signatureImage = null;
                                let signedAt = null;

                                // Handle different data formats
                                if (typeof sigData === 'string') {
                                  console.log(`[Signature] String data, first 50 chars: ${sigData.substring(0, 50)}`);
                                  // Check if it starts with data: (already base64 image)
                                  if (sigData.startsWith('data:image')) {
                                    signatureImage = sigData;
                                  } else if (sigData.startsWith('{')) {
                                    // Looks like JSON
                                    try {
                                      const parsed = JSON.parse(sigData);
                                      signatureImage = parsed.signature || null;
                                      signedAt = parsed.signed_at || null;
                                      console.log(`[Signature] Parsed JSON, has signature: ${!!signatureImage}`);
                                    } catch (e) {
                                      console.error('[Signature] Failed to parse JSON:', e);
                                      signatureImage = sigData;
                                    }
                                  } else {
                                    // Plain base64 or other string
                                    signatureImage = sigData;
                                  }
                                } else if (typeof sigData === 'object' && sigData !== null) {
                                  // Already an object
                                  console.log(`[Signature] Object data, keys: ${Object.keys(sigData).join(', ')}`);
                                  signatureImage = sigData.signature || null;
                                  signedAt = sigData.signed_at || null;
                                }

                                console.log(`[Signature] Final signatureImage starts with: ${signatureImage?.substring(0, 30)}`);

                                if (!signatureImage) return null;

                                // Ensure signature has proper data URI prefix
                                if (signatureImage && !signatureImage.startsWith('data:')) {
                                  console.log('[Signature] Adding data:image prefix');
                                  signatureImage = `data:image/png;base64,${signatureImage}`;
                                }

                                return (
                                  <div className="border border-t-0 border-steel-300 bg-blue-50 px-3 py-2 rounded-b-lg">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <span className="text-xs font-medium text-blue-700">Section Signature</span>
                                        {signedAt && (
                                          <p className="text-xs text-blue-600">
                                            {new Date(signedAt).toLocaleString()}
                                          </p>
                                        )}
                                      </div>
                                      <img
                                        src={signatureImage}
                                        alt="Signature"
                                        className="max-h-12 border border-blue-200 rounded bg-white"
                                        onError={(e) => {
                                          console.error('[Signature] Image failed to load, src was:', e.target.src?.substring(0, 50));
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto bg-steel-100 rounded-full flex items-center justify-center mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-graphite-400">
                        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-graphite-800 mb-2">No Submissions Yet</h3>
                    <p className="text-graphite-500">No isolation flow submissions have been received for this work permit.</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-steel-200 bg-steel-50">
                <div className="flex justify-end">
                  <Button
                    onClick={() => setShowIsolationFlowModal(false)}
                    className="px-6 py-2 rounded-full"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Jeśli status nie jest 'signed', powrót do widoku details
  return null;
};

export default WorkPermitApprovalModal;